import type { RequestDocument } from './requests';

export interface EvidenceFile {
  id: string;
  fileName: string;
  sizeBytes: number;
  createdAt?: string;
}

const path = (requestId: string) => `/api/requests/bug/${encodeURIComponent(requestId)}/evidence/`;
export const evidenceDownloadUrl = (requestId: string, fileId: string) => `${path(requestId)}${encodeURIComponent(fileId)}`;

async function responseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.detail || problem?.error || `Evidence request failed (HTTP ${response.status}).`);
  }
  return response.json() as Promise<T>;
}

export async function listEvidence(requestId: string): Promise<EvidenceFile[]> {
  return responseJson(await fetch(path(requestId), { credentials: 'same-origin' }));
}

export async function uploadEvidence(requestId: string, file: File, uploadId: string): Promise<EvidenceFile> {
  const body = new FormData();
  body.append('file', file);
  body.append('uploadId', uploadId);
  return responseJson(await fetch(path(requestId), { method: 'POST', credentials: 'same-origin', body }));
}

/** Kept by each submission surface until completion, including after partial failures. */
export interface EvidenceSubmission {
  created?: RequestDocument;
  uploadIds: Map<File, string>;
  uploaded: Set<File>;
  pending?: Promise<RequestDocument>;
}

export const newEvidenceSubmission = (): EvidenceSubmission => ({ uploadIds: new Map(), uploaded: new Set() });

export async function submitWithEvidence(
  state: EvidenceSubmission, create: () => Promise<RequestDocument>, files: File[],
): Promise<RequestDocument> {
  if (state.pending) return state.pending;
  state.pending = completeEvidenceSubmission(state, create, files);
  try { return await state.pending; }
  finally { state.pending = undefined; }
}

async function completeEvidenceSubmission(
  state: EvidenceSubmission, create: () => Promise<RequestDocument>, files: File[],
): Promise<RequestDocument> {
  state.created ??= await create();
  for (const file of files) {
    if (state.uploaded.has(file)) continue;
    const uploadId = state.uploadIds.get(file) ?? crypto.randomUUID();
    state.uploadIds.set(file, uploadId);
    try {
      await uploadEvidence(state.created.id, file, uploadId);
      state.uploaded.add(file);
    } catch (error) {
      throw new Error(`Bug saved. "${file.name}" was not confirmed attached. Retry to attach it to the same bug. ${
        error instanceof Error ? error.message : 'Attachment upload failed.'
      }`);
    }
  }
  return state.created;
}
